import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { Button } from '@/components/ui/button';
import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Id } from 'convex/_generated/dataModel';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import { TemplateForm, type TemplateFormValues } from './form';

export const TemplateEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: Id<'templates'> }>();

  // Early validation: ensure id param exists
  if (!id) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-sm text-red-600">
          {t('templates:invalidTemplateId')}
        </div>
      </div>
    );
  }

  return <TemplateEditInner id={id} />;
};

const TemplateEditInner = ({ id }: { id: Id<'templates'> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: template, isLoading } = useQuery(
    convexQuery(api.templates.get, { id }),
  );
  const { mutate: updateTemplate, isPending: isUpdating } = useMutation({
    mutationFn: useConvexMutation(api.templates.update),
  });

  const handleSubmit = async (values: TemplateFormValues) => {
    updateTemplate(
      {
        id,
        name: values.name.trim(),
        language: values.language,
        sections: values.sections,
      },
      {
        onSuccess: () => {
          toast.success(t('templates:templateUpdated'));
          navigate('/templates');
        },
        onError: (error) => {
          toast.error(t('errors:errorUpdatingTemplate'));
          console.error('Error updating template:', error);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col">
        <ResponsiveContainer
          scrollAwareBorder
          className="flex w-full flex-row items-center gap-3 bg-white py-4 md:pt-6">
          <Button
            className="cursor-pointer md:hidden"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}>
            <ChevronLeft className="size-5" />
          </Button>
          <h1 className="text-2xl font-bold">{t('templates:edit')}</h1>
        </ResponsiveContainer>
        <ResponsiveContainer className="flex w-full flex-1 items-center justify-center">
          <Loader2 className="size-8 animate-spin" />
        </ResponsiveContainer>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex min-h-full w-full flex-col">
        <ResponsiveContainer
          scrollAwareBorder
          className="flex w-full flex-row items-center gap-3 bg-white py-4 md:pt-6">
          <Button
            className="cursor-pointer md:hidden"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}>
            <ChevronLeft className="size-5" />
          </Button>
          <h1 className="text-2xl font-bold">{t('templates:edit')}</h1>
        </ResponsiveContainer>
        <ResponsiveContainer className="flex w-full flex-1 items-center justify-center">
          <div className="text-sm text-gray-500">
            {t('templates:templateNotFound')}
          </div>
        </ResponsiveContainer>
      </div>
    );
  }

  const defaultValues: TemplateFormValues = {
    name: template.name,
    language: template.language,
    sections: template.sections,
  };

  return (
    <div className="flex min-h-full w-full flex-col">
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full flex-row items-center gap-3 bg-white py-4 md:pt-6">
        <Button
          className="cursor-pointer md:hidden"
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}>
          <ChevronLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-bold">{t('templates:edit')}</h1>
      </ResponsiveContainer>
      <TemplateForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/templates')}
        isSubmitting={isUpdating}
      />
    </div>
  );
};
